'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { TeacherProfile } from 'types/collections';

import { TeacherService } from '@services/TeacherService';

/**
 * Hook to get the current teacher profile for TEACHER role users
 * Returns the teacher profile data and associated schools for the authenticated teacher user
 */
export function useTeacherProfile() {
  const { data, error, isError, isPending, refetch } = useQuery<TeacherProfile>({
    queryKey: ['teacher-profile'],
    queryFn: () => new TeacherService().getTeacherProfile(),
  });

  const updateMutation = useMutation({
    mutationFn: (profile: Partial<TeacherProfile>) =>
      new TeacherService().updateTeacherProfile(profile),
    onSuccess: () => {
      refetch(); // Refresh the data after successful update
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => new TeacherService().requestAccountDeletion(),
    onSuccess: () => {
      refetch();
    },
  });

  return {
    teacher: data,
    error,
    isError,
    isPending,
    refetch,
    updateTeacherProfile: updateMutation.mutateAsync,
    requestAccountDeletion: deleteAccountMutation.mutateAsync,
    deleteAccountError: deleteAccountMutation.error,
  };
}
